import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, PLATFORM_ID, inject, signal } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { PageShellComponent } from '@shared/components/page-shell/page-shell.component';

interface SectionNavItem {
  id: string;
  labelKey: string;
}

interface Service {
  kickerKey: string;
  titleKey: string;
  bodyKey: string;
}

interface Characteristic {
  titleKey: string;
  shortKey: string;
  descriptionKey: string;
}

interface AnalyticsModel {
  shortKey: string;
  titleKey: string;
  questionKey: string;
  descriptionKey: string;
}

@Component({
  selector: 'app-servicos-page',
  standalone: true,
  imports: [TranslocoPipe, PageShellComponent],
  templateUrl: './servicos.page.html',
})
export class ServicosPage implements AfterViewInit {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly documentHref =
    'mailto:dados@enap.gov.br?subject=Documento%20institucional%20CGDADOS';
  protected readonly breadcrumb = [{ labelKey: 'nav.services' }];

  protected readonly sectionNavItems: SectionNavItem[] = [
    { id: 'servicos', labelKey: 'pages.services.navigation.services' },
    // { id: 'morfologia', labelKey: 'pages.services.navigation.morphology' },
    { id: 'escada', labelKey: 'pages.services.navigation.analytics' },
    { id: 'aprofundar', labelKey: 'pages.services.navigation.document' },
  ];

  protected readonly services: Service[] = [
    {
      kickerKey: 'pages.services.services.items.access.kicker',
      titleKey: 'pages.services.services.items.access.title',
      bodyKey: 'pages.services.services.items.access.body',
    },
    {
      kickerKey: 'pages.services.services.items.analysis.kicker',
      titleKey: 'pages.services.services.items.analysis.title',
      bodyKey: 'pages.services.services.items.analysis.body',
    },
    {
      kickerKey: 'pages.services.services.items.visualization.kicker',
      titleKey: 'pages.services.services.items.visualization.title',
      bodyKey: 'pages.services.services.items.visualization.body',
    },
  ];

  /*
  protected readonly characteristics: Characteristic[] = [
    {
      titleKey: 'pages.services.morphology.items.type.title',
      shortKey: 'pages.services.morphology.items.type.short',
      descriptionKey: 'pages.services.morphology.items.type.description',
    },
    {
      titleKey: 'pages.services.morphology.items.accessibility.title',
      shortKey: 'pages.services.morphology.items.accessibility.short',
      descriptionKey: 'pages.services.morphology.items.accessibility.description',
    },
    {
      titleKey: 'pages.services.morphology.items.quality.title',
      shortKey: 'pages.services.morphology.items.quality.short',
      descriptionKey: 'pages.services.morphology.items.quality.description',
    },
    {
      titleKey: 'pages.services.morphology.items.volume.title',
      shortKey: 'pages.services.morphology.items.volume.short',
      descriptionKey: 'pages.services.morphology.items.volume.description',
    },
  ];
  */

  protected readonly analyticsModels: AnalyticsModel[] = [
    {
      shortKey: 'pages.services.analytics.items.descriptive.short',
      titleKey: 'pages.services.analytics.items.descriptive.title',
      questionKey: 'pages.services.analytics.items.descriptive.question',
      descriptionKey: 'pages.services.analytics.items.descriptive.description',
    },
    {
      shortKey: 'pages.services.analytics.items.inferential.short',
      titleKey: 'pages.services.analytics.items.inferential.title',
      questionKey: 'pages.services.analytics.items.inferential.question',
      descriptionKey: 'pages.services.analytics.items.inferential.description',
    },
    {
      shortKey: 'pages.services.analytics.items.predictive.short',
      titleKey: 'pages.services.analytics.items.predictive.title',
      questionKey: 'pages.services.analytics.items.predictive.question',
      descriptionKey: 'pages.services.analytics.items.predictive.description',
    },
    {
      shortKey: 'pages.services.analytics.items.generative.short',
      titleKey: 'pages.services.analytics.items.generative.title',
      questionKey: 'pages.services.analytics.items.generative.question',
      descriptionKey: 'pages.services.analytics.items.generative.description',
    },
  ];

  protected readonly activeSection = signal(this.sectionNavItems[0].id);

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const elements = this.sectionNavItems
      .map(({ id }) => document.getElementById(id))
      .filter((element): element is HTMLElement => element !== null);

    if (elements.length === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((first, second) => second.intersectionRatio - first.intersectionRatio);

        const activeEntry = visibleEntries[0];
        if (activeEntry) {
          this.activeSection.set(activeEntry.target.id);
        }
      },
      {
        rootMargin: '-30% 0px -55% 0px',
        threshold: [0, 0.25, 0.5, 1],
      },
    );

    elements.forEach((element) => observer.observe(element));
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  protected selectSection(event: Event): void {
    const id = (event.target as HTMLSelectElement).value;
    this.scrollToSection(id);
  }

  protected scrollToSection(id: string, event?: Event): void {
    event?.preventDefault();
    this.activeSection.set(id);
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  protected formatIndex(index: number): string {
    return String(index + 1).padStart(2, '0');
  }
}
